<template>
    <div>
        <div class="mb-4 flex items-center justify-between space-x-2">
            <h1 class="text-lg font-semibold text-gray-700">Pricing</h1>
            <UButton @click="editing = true" color="black" icon="i-ph-pencil-bold" v-if="!editing && product">
                Edit Pricing
            </UButton>
        </div>

        <div v-if="loading" class="flex items-center justify-center py-12">
            <span class="text-sm text-gray-400">Loading...</span>
        </div>

        <div v-else-if="!product" class="flex flex-col items-center justify-center py-12 space-y-1">
            <UIcon name="i-ph-package-duotone" class="size-5 text-gray-400" />
            <span class="text-gray-400 text-sm">No product configured.</span>
        </div>

        <template v-else>
            <div v-if="!editing" class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                <table class="w-full text-sm">
                    <thead>
                        <tr class="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                            <th class="px-4 py-3">Members</th>
                            <th class="px-4 py-3 text-right">Price / Member (incl. VAT)</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="tier in sortedTiers" :key="tier.id">
                            <td class="px-4 py-3 font-medium">
                                {{ tier.to_quantity == null ? `${tier.from_quantity}+` : tier.from_quantity === tier.to_quantity ? tier.from_quantity : `${tier.from_quantity}–${tier.to_quantity}` }}
                            </td>
                            <td class="px-4 py-3 text-right font-mono">${{ centsToDollars(tier.unit_price) }}</td>
                        </tr>
                        <tr v-if="!sortedTiers.length">
                            <td colspan="2" class="px-4 py-8 text-center text-gray-400">No pricing tiers configured.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-else class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm p-5 space-y-4">
                <div class="bg-gray-50 rounded-lg overflow-hidden">
                    <table class="w-full text-sm">
                        <thead>
                            <tr class="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                                <th class="px-3 py-2">From</th>
                                <th class="px-3 py-2">To</th>
                                <th class="px-3 py-2">$/member (incl. VAT)</th>
                                <th class="px-3 py-2 w-px"></th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200">
                            <tr v-for="(tier, i) in tierRows" :key="i">
                                <td class="px-3 py-1.5">
                                    <UInput v-model.number="tier.from_quantity" type="number" size="sm" min="1" :ui="{ base: 'font-mono' }" />
                                </td>
                                <td class="px-3 py-1.5">
                                    <UInput :model-value="tier.to_quantity" @update:model-value="tier.to_quantity = $event === '' || $event == null ? null : Number($event)" type="number" size="sm" min="1" placeholder="∞" :ui="{ base: 'font-mono' }" />
                                </td>
                                <td class="px-3 py-1.5">
                                    <UInput v-model="tier.price_display" type="text" size="sm" placeholder="0.00" :ui="{ base: 'font-mono' }" @blur="onPriceBlur(tier)" />
                                </td>
                                <td class="px-3 py-1.5">
                                    <UButton color="gray" variant="ghost" icon="i-ph-trash-fill" size="xs" :ui="{ icon: { size: { xs: 'text-red-400' } } }" @click="tierRows.splice(i, 1)" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="!tierRows.length" class="text-sm text-gray-400 text-center py-6">
                        No pricing tiers. Click Add to create one.
                    </div>
                </div>

                <div class="flex items-center justify-between">
                    <UButton @click="addTierRow" color="white" icon="i-ph-plus-bold" size="sm">Add Tier</UButton>
                    <div class="flex items-center space-x-2">
                        <UButton @click="cancelEdit" size="sm" color="white">Cancel</UButton>
                        <UButton @click="save" size="sm" color="black" :loading="saving" :disabled="!tierRows.length">Save</UButton>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { Product, ProductPricingTier } from '~/types/product'
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Settings – Pricing' })

const client = useSupabaseClient()
const toast = useToast()

const loading = ref(false)
const product = ref<Product>()
const editing = ref(false)
const saving = ref(false)

interface TierRow { from_quantity: number; to_quantity: number | null; unit_price: number; price_display: string }
const tierRows = ref<TierRow[]>([])

const sortedTiers = computed(() =>
    [...(product.value?.pricing_tiers || [])].sort((a, b) => a.from_quantity - b.from_quantity)
)

onMounted(() => fetchProduct())

async function fetchProduct() {
    loading.value = true
    const { data } = await client
        .from('products')
        .select('*, pricing_tiers:product_pricing_tiers(*)')
        .is('deleted_at', null)
        .limit(1)
        .maybeSingle()
    if (data) product.value = data as any
    loading.value = false
}

function centsToDollars(cents: number): string {
    return (cents / 100).toFixed(2)
}

function dollarsToCents(dollars: string): number {
    return Math.round(parseFloat(dollars || '0') * 100)
}

function onPriceBlur(tier: TierRow) {
    const cents = dollarsToCents(tier.price_display)
    tier.unit_price = cents
    tier.price_display = centsToDollars(cents)
}

watch(editing, (val) => {
    if (val) {
        tierRows.value = sortedTiers.value.map(t => ({
            from_quantity: t.from_quantity,
            to_quantity: t.to_quantity,
            unit_price: t.unit_price,
            price_display: centsToDollars(t.unit_price),
        }))
    }
})

function addTierRow() {
    const last = tierRows.value[tierRows.value.length - 1]
    const nextFrom = last ? (last.to_quantity != null ? last.to_quantity + 1 : last.from_quantity + 1) : 1
    tierRows.value.push({
        from_quantity: nextFrom,
        to_quantity: null,
        unit_price: 0,
        price_display: '0.00',
    })
}

function cancelEdit() {
    editing.value = false
    tierRows.value = []
}

async function save() {
    if (!product.value) return
    saving.value = true

    await client.from('product_pricing_tiers').delete().eq('product_id', product.value.id)

    if (tierRows.value.length) {
        const { error } = await client.from('product_pricing_tiers').insert(
            tierRows.value.map(row => ({
                product_id: product.value!.id,
                from_quantity: row.from_quantity,
                to_quantity: row.to_quantity,
                unit_price: row.unit_price,
            })) as never
        )

        if (error) {
            toast.add({ title: 'Could not save pricing.', color: 'red', icon: 'i-ph-warning-duotone' })
            saving.value = false
            return
        }
    }

    toast.add({ title: 'Pricing saved.', color: 'green', icon: 'i-ph-check-circle-duotone' })
    editing.value = false
    saving.value = false
    await fetchProduct()
}
</script>
